import {
  IAnalyticsChartsDataCollector
} from "@ispm/widgets/components/analytics-charts/interfaces/analytics-charts-data-collector.interface";
import {
  AnalyticsChartSortOrder,
  IAnalyticsChartsConfig
} from "@ispm/widgets/components/analytics-charts/interfaces/analytics-charts-config.interface";

export interface IAnalyticsChartsData {
  items: Array<IAnalyticsChartDataItem>;
}

export interface IAnalyticsChartDataItem {
  categories: Array<AnalyticsChartCategory>;
  population?: number;
}

export interface AnalyticsChartCategoryComparator {
  compare: (cat1: AnalyticsChartCategory, cat2: AnalyticsChartCategory, sortFactor: AnalyticsChartSortOrder) => number;
}

export class AnalyticsChartCategory {
  label: string;
  count: number;
  percentage?: string;
  metricLabel?: string;
  compareBy?: any; // can be a specific value or an instance of AnalyticsChartCategoryComparator

  constructor(label: string, count?: number, percentage?: string, metricLabel?: string) {
    this.label = label;
    this.count = count;
    this.percentage = percentage;
    this.metricLabel = metricLabel;
  }
}

/**
 * This interface is implemented by the AnalyticsChartsComponent.
 * The component can interact with any source component, which implements the IAnalyticsChartDataSource interface. On loading, the widget register itself to the source component and it can be invoked through the methods it exposes.
 */
export interface IAnalyticsChartDataTarget {

  /**
   * return a list of IDs for all columns for which it is required to collect data
   */
  getCollectibleColumns: () => Array<string>;

  /**
   * this event is invoked whenever charts' configuration is changed
   */
  onConfigChanged: (chartsConfig: IAnalyticsChartsConfig) => void;

  /**
   * this event is invoked after all data has been collected for the given column
   */
  onDataChanged: (columnId: string, eventData: IAnalyticsChartDataItem) => void;

  /**
   * this method is invoked to set the charts' header.
   * Data can contain any of the following parameters:
   *    @header (optional) provide a value to override the configured header value.
   *            If not provided, configured value is used
   *    @prefix (optional) add a prefix to current header's value
   *    @suffix (optional) add a suffix to current header's value
   */
  setHeader: (data: any) => void;

  /**
   * this method factors and returns a specific data collector for the given column. The data collector gathers the categories and values that feed the chart.
   * The collected categories are not changed once they were concluded for the first time.
   */
  factorColumnDataCollector: (columnId: string, dataType: string) => IAnalyticsChartsDataCollector;
}

export interface IAnalyticsChartDataSource {
  /**
   * On loading, IAnalyticsChartDataTarget registers itself to the source component>
   * later on, it can be invoked through the methods it exposes
   */
  registerChartDataTarget: (target: IAnalyticsChartDataTarget) => void;
}

/**
 * an empty implementation of IAnalyticsChartDataTarget. It can be used for initialization
 */
export const noChartDataTarget: IAnalyticsChartDataTarget = {
  getCollectibleColumns(): Array<string> {return []},
  onDataChanged(columnId: string, eventData: IAnalyticsChartDataItem) {},
  onConfigChanged(chartsConfig: IAnalyticsChartsConfig) {},
  factorColumnDataCollector(columnId: string, dataType: string) {return null;},
  setHeader(data: any) {}
}
